import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Typography,
  Box,
  FormHelperText,
  CircularProgress,
  Fade,
  Divider,
  IconButton,
  Tooltip,
  useTheme,
  Alert
} from '@mui/material';
import { Close, PersonAdd, Info } from '@mui/icons-material';

const AddUser = ({ open, onClose, onSubmit, branches, isLoading }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  
  // Form state - FIXED: Consistent field names
  const [formData, setFormData] = useState({
    username: '',      // ← FIXED: Use username instead of userName
    firstName: '',     // ← FIXED: Use firstName instead of first_name
    lastName: '',      // ← FIXED: Use lastName instead of last_name
    email: '',
    branchId: '',
    roleId: ''
  });
  
  // Validation state
  const [errors, setErrors] = useState({});
  const [generalError, setGeneralError] = useState(''); // ← FIXED: Add missing setError state
  
  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: ''
      }));
    }
    
    // Clear general error
    if (generalError) {
      setGeneralError('');
    }
  };
  
  // Validate form - FIXED: Use correct field names and actually call it
  const validateForm = () => {
    const newErrors = {};
    
    // Required fields - FIXED: Match formData field names
    if (!formData.username?.trim()) newErrors.username = t('user.add.usernameRequired');
    if (!formData.firstName?.trim()) newErrors.firstName = t('user.add.firstNameRequired');
    if (!formData.lastName?.trim()) newErrors.lastName = t('user.add.lastNameRequired');
    if (!formData.email?.trim()) {
      newErrors.email = t('user.add.emailRequired');
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t('user.add.emailInvalid');
    }
    if (!formData.branchId) newErrors.branchId = t('user.add.branchRequired');
    if (!formData.roleId) newErrors.roleId = t('user.add.roleRequired');
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // Handle submit - FIXED: Complete rewrite
  const handleSubmit = () => {
    // Clear previous errors
    setGeneralError('');
    
    // Validate form first
    if (!validateForm()) {
      setGeneralError(t('user.add.pleaseFixErrors'));
      return;
    }
    
    // **FIXED: Use correct field names from formData**
    const requiredFields = {
      username: formData.username?.trim(),
      email: formData.email?.trim(),
      firstName: formData.firstName?.trim(),
      lastName: formData.lastName?.trim(),
      branchId: formData.branchId,
      roleId: formData.roleId
    };

    console.log('🔍 Form data being sent:', requiredFields);

    // Double-check for missing required fields
    const missingFields = Object.entries(requiredFields)
      .filter(([, value]) => !value) // ← FIXED: Remove unused 'key' parameter
      .map(([key]) => key);

    if (missingFields.length > 0) {
      console.error('❌ Missing required fields:', missingFields);
      setGeneralError(`Missing required fields: ${missingFields.join(', ')}`);
      return;
    }

    // **FIXED: Make sure all fields are properly mapped**
    const userData = {
      username: requiredFields.username,
      email: requiredFields.email,
      firstName: requiredFields.firstName,
      lastName: requiredFields.lastName,
      branchId: parseInt(requiredFields.branchId),
      roleId: parseInt(requiredFields.roleId)
    };

    console.log('📤 Sending user data:', userData);
    onSubmit(userData);
  };
  
  // Handle dialog close and reset form
  const handleClose = () => {
    setFormData({
      username: '',    // ← FIXED: Match field names
      firstName: '',   // ← FIXED: Match field names
      lastName: '',    // ← FIXED: Match field names
      email: '',
      branchId: '',
      roleId: ''
    });
    setErrors({});
    setGeneralError('');
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: { 
          borderRadius: 2,
          overflow: 'hidden',
          backgroundColor: isDarkMode ? '#1e1e1e' : '#ffffff',
        }
      }}
    >
      <DialogTitle 
        sx={{ 
          background: isDarkMode 
            ? 'linear-gradient(135deg, #4a5568 0%, #553c8b 100%)'
            : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonAdd />
          <Typography variant="h6" sx={{ color: 'white' }}>
            {t('user.add.title')}
          </Typography>
        </Box>
        <IconButton size="small" onClick={handleClose} sx={{ color: 'white' }}>
          <Close />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ 
        p: 3,
        backgroundColor: isDarkMode ? '#1e1e1e' : '#ffffff',
        color: isDarkMode ? '#ffffff' : 'inherit'
      }}>
        {/* General Error Alert */}
        {generalError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {generalError}
          </Alert>
        )}
        
        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Typography 
                variant="subtitle1" 
                fontWeight={600} 
                sx={{ color: isDarkMode ? '#ffffff' : theme.palette.primary.main }}
              >
                {t('user.add.accountInfo')}
              </Typography>
              <Divider sx={{ flexGrow: 1, ml: 2, borderColor: isDarkMode ? '#404040' : 'inherit' }} />
            </Box>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t('user.add.username')}
              name="username" // ← FIXED: Match formData field name
              value={formData.username}
              onChange={handleChange}
              error={!!errors.username}
              helperText={errors.username}
              required
              autoFocus
              InputProps={{
                sx: { 
                  borderRadius: 1.5,
                  backgroundColor: isDarkMode ? '#2a2a2a' : '#ffffff',
                  color: isDarkMode ? '#ffffff' : 'inherit',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: isDarkMode ? '#404040' : 'rgba(0, 0, 0, 0.23)',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: isDarkMode ? '#667eea' : 'rgba(0, 0, 0, 0.87)',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#667eea',
                  }
                }
              }}
              InputLabelProps={{
                sx: { color: isDarkMode ? '#b3b3b3' : 'inherit' }
              }}
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t('user.add.email')}
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              error={!!errors.email}
              helperText={errors.email}
              required
              InputProps={{
                sx: { 
                  borderRadius: 1.5,
                  backgroundColor: isDarkMode ? '#2a2a2a' : '#ffffff',
                  color: isDarkMode ? '#ffffff' : 'inherit',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: isDarkMode ? '#404040' : 'rgba(0, 0, 0, 0.23)',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: isDarkMode ? '#667eea' : 'rgba(0, 0, 0, 0.87)',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#667eea',
                  }
                }
              }}
              InputLabelProps={{
                sx: { color: isDarkMode ? '#b3b3b3' : 'inherit' }
              }}
            />
          </Grid>
          
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, mt: 1 }}>
              <Typography 
                variant="subtitle1" 
                fontWeight={600} 
                sx={{ color: isDarkMode ? '#ffffff' : theme.palette.primary.main }}
              >
                {t('user.add.personalInfo')}
              </Typography>
              <Divider sx={{ flexGrow: 1, ml: 2, borderColor: isDarkMode ? '#404040' : 'inherit' }} />
            </Box>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t('user.add.firstName')}
              name="firstName" // ← FIXED: Match formData field name
              value={formData.firstName}
              onChange={handleChange}
              error={!!errors.firstName}
              helperText={errors.firstName}
              required
              InputProps={{
                sx: { 
                  borderRadius: 1.5,
                  backgroundColor: isDarkMode ? '#2a2a2a' : '#ffffff',
                  color: isDarkMode ? '#ffffff' : 'inherit',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: isDarkMode ? '#404040' : 'rgba(0, 0, 0, 0.23)',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: isDarkMode ? '#667eea' : 'rgba(0, 0, 0, 0.87)',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#667eea',
                  }
                }
              }}
              InputLabelProps={{
                sx: { color: isDarkMode ? '#b3b3b3' : 'inherit' }
              }}
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t('user.add.lastName')}
              name="lastName" // ← FIXED: Match formData field name
              value={formData.lastName}
              onChange={handleChange}
              error={!!errors.lastName}
              helperText={errors.lastName}
              required
              InputProps={{
                sx: { 
                  borderRadius: 1.5,
                  backgroundColor: isDarkMode ? '#2a2a2a' : '#ffffff',
                  color: isDarkMode ? '#ffffff' : 'inherit',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: isDarkMode ? '#404040' : 'rgba(0, 0, 0, 0.23)',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: isDarkMode ? '#667eea' : 'rgba(0, 0, 0, 0.87)',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#667eea',
                  }
                }
              }}
              InputLabelProps={{
                sx: { color: isDarkMode ? '#b3b3b3' : 'inherit' }
              }}
            />
          </Grid>
          
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, mt: 1 }}>
              <Typography 
                variant="subtitle1" 
                fontWeight={600} 
                sx={{ color: isDarkMode ? '#ffffff' : theme.palette.primary.main }}
              >
                {t('user.add.roleAndBranch')}
              </Typography>
              <Divider sx={{ flexGrow: 1, ml: 2, borderColor: isDarkMode ? '#404040' : 'inherit' }} />
            </Box>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth error={!!errors.branchId} required>
              <InputLabel sx={{ color: isDarkMode ? '#b3b3b3' : 'inherit' }}>
                {t('user.branch')}
              </InputLabel>
              <Select
                name="branchId"
                value={formData.branchId}
                onChange={handleChange}
                label={t('user.branch')}
                sx={{ 
                  borderRadius: 1.5,
                  backgroundColor: isDarkMode ? '#2a2a2a' : '#ffffff',
                  color: isDarkMode ? '#ffffff' : 'inherit',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: isDarkMode ? '#404040' : 'rgba(0, 0, 0, 0.23)',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: isDarkMode ? '#667eea' : 'rgba(0, 0, 0, 0.87)',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#667eea',
                  }
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      backgroundColor: isDarkMode ? '#2a2a2a' : '#ffffff',
                      '& .MuiMenuItem-root': {
                        color: isDarkMode ? '#ffffff' : 'inherit',
                        '&:hover': {
                          backgroundColor: isDarkMode ? '#404040' : 'rgba(0, 0, 0, 0.04)',
                        }
                      }
                    }
                  }
                }}
              >
                {branches.map(branch => (
                  <MenuItem key={branch.branch_id} value={branch.branch_id.toString()}>
                    {branch.location_name}
                  </MenuItem>
                ))}
              </Select>
              {errors.branchId && (
                <FormHelperText>{errors.branchId}</FormHelperText>
              )}
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth error={!!errors.roleId} required>
              <InputLabel sx={{ color: isDarkMode ? '#b3b3b3' : 'inherit' }}>
                {t('user.role')}
              </InputLabel>
              <Select
                name="roleId"
                value={formData.roleId}
                onChange={handleChange}
                label={t('user.role')}
                sx={{ 
                  borderRadius: 1.5,
                  backgroundColor: isDarkMode ? '#2a2a2a' : '#ffffff',
                  color: isDarkMode ? '#ffffff' : 'inherit',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: isDarkMode ? '#404040' : 'rgba(0, 0, 0, 0.23)',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: isDarkMode ? '#667eea' : 'rgba(0, 0, 0, 0.87)',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#667eea',
                  }
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      backgroundColor: isDarkMode ? '#2a2a2a' : '#ffffff',
                      '& .MuiMenuItem-root': {
                        color: isDarkMode ? '#ffffff' : 'inherit',
                        '&:hover': {
                          backgroundColor: isDarkMode ? '#404040' : 'rgba(0, 0, 0, 0.04)',
                        }
                      }
                    }
                  }
                }}
              >
                <MenuItem value="1">{t('user.admin')}</MenuItem>
                <MenuItem value="2">{t('user.manager')}</MenuItem>
                <MenuItem value="3">{t('user.staff')}</MenuItem>
              </Select>
              {errors.roleId && (
                <FormHelperText>{errors.roleId}</FormHelperText>
              )}
            </FormControl>
          </Grid>
        </Grid>
        
        <Box sx={{ 
          mt: 3, 
          p: 2, 
          borderRadius: 1.5, 
          backgroundColor: isDarkMode 
            ? 'rgba(102, 126, 234, 0.1)' 
            : theme.palette.info.light + '20',
          borderLeft: `4px solid ${theme.palette.info.main}`,
          border: isDarkMode ? '1px solid rgba(102, 126, 234, 0.3)' : undefined
        }}>
          <Typography variant="body2" sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            color: isDarkMode ? '#ffffff' : 'inherit'
          }}>
            <Info fontSize="small" color="info" />
            {t('user.add.passwordNote')}
          </Typography>
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ 
        p: 2, 
        backgroundColor: isDarkMode ? '#2a2a2a' : theme.palette.grey[50],
        borderTop: `1px solid ${isDarkMode ? '#404040' : 'rgba(0, 0, 0, 0.12)'}`
      }}>
        <Button 
          onClick={handleClose}
          variant="outlined"
          sx={{ 
            borderRadius: 1.5,
            color: isDarkMode ? '#ffffff' : 'inherit',
            borderColor: isDarkMode ? '#404040' : 'rgba(0, 0, 0, 0.23)',
            '&:hover': {
              borderColor: isDarkMode ? '#667eea' : 'rgba(0, 0, 0, 0.87)',
              backgroundColor: isDarkMode ? 'rgba(102, 126, 234, 0.1)' : 'rgba(0, 0, 0, 0.04)',
            }
          }}
          disabled={isLoading}
        >
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isLoading}
          sx={{ 
            borderRadius: 1.5,
            background: isDarkMode
              ? 'linear-gradient(135deg, #4a5568 0%, #553c8b 100%)'
              : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            '&:hover': {
              background: isDarkMode
                ? 'linear-gradient(135deg, #3a4454 0%, #453177 100%)'
                : 'linear-gradient(135deg, #5a71d6 0%, #6a4292 100%)'
            }
          }}
          startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <PersonAdd />}
        >
          {isLoading ? t('user.add.creating') : t('user.add.createButton')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddUser;